<script setup>
// ============================================================================
// BonBoZ: bảng "Thống kê chi tiết tương tác" — ĐẦU BẢNG HAI TẦNG, đúng khuôn
// Pancake: hai nhóm cột "Tương Tác khách hàng" và "Đơn hàng đã chốt".
//
// HAI CỘT TỔNG ĐƯỢC VIỀN NỔI vì đầu bảng ghi thẳng công thức "(1) + (2)" —
// người đọc phải nhìn ra ngay ba cột nào đi với nhau. Viền chạy suốt từ đầu
// bảng xuống dòng Tổng, nếu không thì nó chỉ là một cái ô tô màu.
//
// BA CỘT TỈ LỆ TÍNH TẠI CHỖ, mỗi cột chia trong đúng rổ của nó:
//   Tỉ lệ CĐ trung bình = tong_dh ÷ tong_tt
//   Tỉ lệ CĐ KH mới     = dh_moi  ÷ tt_moi
//   Tỉ lệ CĐ KH cũ      = dh_cu   ÷ tt_cu
// Chia chéo rổ (đơn của khách mới cho tổng tương tác) là cách chắc chắn ra một
// con số vô nghĩa — đúng cái bẫy đã làm trang Tổng quan hiện 600%.
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ngay } from 'dashboard/helper/dinhDangNgay';
import {
  taiXuongExcel,
  tenFileThongKe,
} from 'dashboard/helper/bonboz/xuatExcel';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  dong: { type: Array, default: () => [] },
  tong: { type: Object, default: null },
  gom: { type: String, default: 'thoi_gian' },
  gomGio: { type: String, default: 'ngay' },
  dangTai: { type: Boolean, default: false },
});
const emit = defineEmits(['doiGom', 'doiGomGio']);

const { t } = useI18n();

// `nhom` gom cột vào hai tầng đầu bảng; `noi_bat` là hai cột tổng có viền.
const COT = [
  { khoa: 'tt_cu', nhom: 'TT' },
  { khoa: 'tt_moi', nhom: 'TT' },
  { khoa: 'tong_tt', nhom: 'TT', noi_bat: true },
  { khoa: 'tin_nhan', nhom: 'TT' },
  { khoa: 'binh_luan', nhom: 'TT' },
  { khoa: 'hoi_thoai_moi', nhom: 'TT' },
  { khoa: 'dh_moi', nhom: 'DH' },
  { khoa: 'dh_cu', nhom: 'DH' },
  { khoa: 'tong_dh', nhom: 'DH', noi_bat: true },
  { khoa: 'ti_le_tb', nhom: 'DH', ti_le: ['tong_dh', 'tong_tt'] },
  { khoa: 'ti_le_moi', nhom: 'DH', ti_le: ['dh_moi', 'tt_moi'] },
  { khoa: 'ti_le_cu', nhom: 'DH', ti_le: ['dh_cu', 'tt_cu'] },
];

// ------------------------------------------------------------- chọn cột
// Cùng khuôn với hai bảng kia; lý do đầy đủ ở `BangTheoNgay.vue`.
const KHOA_LUU = 'bonboz_cot_tuong_tac';
const doc = () => {
  try {
    const luu = JSON.parse(localStorage.getItem(KHOA_LUU));
    return Array.isArray(luu) && luu.length ? luu : null;
  } catch {
    return null;
  }
};
const cotHien = ref(doc() || COT.map(c => c.khoa));
watch(cotHien, giaTri => {
  try {
    localStorage.setItem(KHOA_LUU, JSON.stringify(giaTri));
  } catch {
    // Trình duyệt chặn lưu trữ thì bảng vẫn chạy, chỉ là mở lại phải chọn lại.
  }
});
const moChonCot = ref(false);
const cot = computed(() => COT.filter(c => cotHien.value.includes(c.khoa)));

// Số cột của từng nhóm ở tầng đầu bảng trên cùng — phải đếm lại theo những cột
// đang HIỆN, nếu không thì bỏ một cột là hai nhóm lệch hẳn khỏi thân bảng.
const soCotNhom = computed(() => ({
  TT: cot.value.filter(c => c.nhom === 'TT').length,
  DH: cot.value.filter(c => c.nhom === 'DH').length,
}));

const doiCot = khoa => {
  const co = cotHien.value.includes(khoa);
  if (co && cotHien.value.length === 1) return;
  cotHien.value = co
    ? cotHien.value.filter(k => k !== khoa)
    : COT.map(c => c.khoa).filter(k => cotHien.value.includes(k) || k === khoa);
};

// -------------------------------------------------------------- giá trị ô
// `null` khi mẫu bằng 0: "0%" nói rằng đã đo và ra không, còn ở đây là chưa có
// gì để đo. Hai chuyện khác nhau.
const oCua = (nguon, c) => {
  if (!nguon) return null;
  if (!c.ti_le) return nguon[c.khoa] ?? 0;
  const [tu, mau] = c.ti_le;
  const duoi = nguon[mau] ?? 0;
  if (!duoi) return null;
  return `${Math.round(((nguon[tu] ?? 0) / duoi) * 1000) / 10}%`;
};

const hang = computed(() => {
  if (props.gom === 'nhan_vien') return props.dong;
  // Giờ: 0h → 23h, thứ tự của một ngày. Ngày: mới nhất lên đầu.
  const ds = [...props.dong];
  return props.gomGio === 'gio' ? ds : ds.sort((a, b) => b.moc - a.moc);
});

const nhanDong = d => {
  if (props.gom === 'nhan_vien') return d.ten;
  return props.gomGio === 'gio'
    ? t('BONBOZ.TRANG_TK.KHUNG_GIO', { tu: d.moc, den: (d.moc + 1) % 24 })
    : ngay(d.moc * 1000, 'ngayDay');
};

// ------------------------------------------------------------------ Excel
// Xuất ĐÚNG cái bảng đang hiện. Đầu bảng dẹp thành MỘT dòng: Excel không có
// đầu bảng hai tầng, nên tên nhóm ghép vào tên cột để không mất ngữ cảnh.
const xuatExcel = () => {
  const cacHang = [
    [
      t(`BONBOZ.TUONG_TAC.COT_${props.gom === 'nhan_vien' ? 'NGUOI' : 'LUC'}`),
      ...cot.value.map(
        c =>
          `${t(`BONBOZ.TUONG_TAC.NHOM_${c.nhom}`)} · ${t(
            `BONBOZ.TUONG_TAC.C_${c.khoa}`
          )}`
      ),
    ],
    ...hang.value.map(d => [
      nhanDong(d),
      ...cot.value.map(c => oCua(d, c) ?? ''),
    ]),
  ];
  if (props.tong) {
    cacHang.push([
      t('BONBOZ.TUONG_TAC.TONG_TT'),
      ...cot.value.map(c => oCua(props.tong, c) ?? ''),
    ]);
  }
  const moc =
    props.gom === 'thoi_gian' && props.gomGio === 'ngay'
      ? props.dong.map(d => d.moc)
      : [];
  taiXuongExcel(
    tenFileThongKe(
      `thong-ke-tuong-tac-${props.gom}`,
      moc.length ? Math.min(...moc) : null,
      moc.length ? Math.max(...moc) : null
    ),
    cacHang
  );
};

const TAB = [
  { khoa: 'thoi_gian', nhan: 'TAB_THOI_GIAN' },
  { khoa: 'nhan_vien', nhan: 'TAB_NHAN_VIEN' },
];
const TAB_GIO = [
  { khoa: 'ngay', nhan: 'TAB_NGAY' },
  { khoa: 'gio', nhan: 'TAB_GIO' },
];

// Viền nổi của hai cột tổng, dùng lại ở cả ba tầng (đầu bảng, thân, chân) để
// nó chạy thành một khung liền chứ không phải ba mảnh rời.
const vien = c =>
  c.noi_bat ? 'border-x border-n-brand bg-[rgb(var(--bonboz-mo)/0.04)]' : '';
</script>

<template>
  <section class="border rounded-xl border-n-weak bg-n-solid-2">
    <div class="p-4 pb-0">
      <h3 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ $t('BONBOZ.TUONG_TAC.CHI_TIET') }}
      </h3>
      <p class="m-0 mt-0.5 text-xs text-n-slate-11">
        {{ $t('BONBOZ.TUONG_TAC.CHI_TIET_MO_TA') }}
      </p>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3 px-4 pt-3">
      <!-- Tab gạch chân cho hai cách gom; tab viên thuốc cho ngày/giờ. Hai kiểu
           khác nhau vì chúng ở hai cấp: đổi nguồn dòng, và đổi độ mịn. -->
      <div class="flex gap-5 -mb-px">
        <button
          v-for="x in TAB"
          :key="x.khoa"
          type="button"
          class="pb-2 text-sm transition-colors border-b-2"
          :class="
            gom === x.khoa
              ? 'border-n-brand text-n-brand font-medium'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="emit('doiGom', x.khoa)"
        >
          {{ $t(`BONBOZ.TUONG_TAC.${x.nhan}`) }}
        </button>
      </div>

      <div class="flex items-center gap-2 pb-2">
        <!-- Ngày/giờ chỉ có nghĩa ở tab Thời gian. -->
        <div
          v-if="gom === 'thoi_gian'"
          class="flex p-0.5 rounded-lg bg-n-alpha-1"
        >
          <button
            v-for="x in TAB_GIO"
            :key="x.khoa"
            type="button"
            class="px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="
              gomGio === x.khoa
                ? 'bg-n-brand text-white font-medium'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="emit('doiGomGio', x.khoa)"
          >
            {{ $t(`BONBOZ.TRANG_TK.${x.nhan}`) }}
          </button>
        </div>

        <div class="relative">
          <Button
            variant="outline"
            color="slate"
            size="sm"
            icon="i-lucide-columns-3"
            :label="$t('BONBOZ.TRANG_TK.TUY_CHINH')"
            @click="moChonCot = !moChonCot"
          />
          <div
            v-if="moChonCot"
            class="fixed inset-0 z-10"
            @click="moChonCot = false"
          />
          <div
            v-if="moChonCot"
            class="absolute right-0 z-20 flex flex-col gap-1 p-2 mt-1 overflow-y-auto border shadow-lg w-64 max-h-80 rounded-xl border-n-weak bg-n-solid-1"
          >
            <label
              v-for="c in COT"
              :key="c.khoa"
              class="flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
            >
              <input
                type="checkbox"
                :checked="cotHien.includes(c.khoa)"
                class="accent-n-brand"
                @change="doiCot(c.khoa)"
              />
              {{ $t(`BONBOZ.TUONG_TAC.C_${c.khoa}`) }}
            </label>
          </div>
        </div>

        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-download"
          :label="$t('BONBOZ.XUAT.NUT')"
          :disabled="!hang.length"
          @click="xuatExcel"
        />
      </div>
    </div>

    <div
      v-if="dangTai"
      class="m-4 rounded-lg h-80 bg-n-alpha-2 animate-pulse"
    />

    <!-- Bảng tự cuộn cả hai chiều trong khung của nó; trang không bao giờ cuộn
         ngang. Đầu bảng và cột đầu dính lại khi cuộn. -->
    <div v-else class="overflow-auto max-h-[32rem] border-t border-n-weak">
      <!-- BonBoZ: boc de cuon ngang. Bang nhieu cot hon be ngang man thi bi CAT
           cung chu khong cuon — tren dien thoai mat han cac cot ben phai. Hai man
           Cam xuc va Quang cao da boc dung tu truoc, day la lam not cho deu. -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead class="sticky top-0 z-10 bg-n-slate-2">
            <!-- Tầng 1: tên hai nhóm. Ô đầu `rowspan=2` để cột thời gian/tên
                 người chạy suốt cả hai tầng, đúng như Pancake. -->
            <tr class="text-xs text-n-slate-11">
              <th
                rowspan="2"
                class="sticky left-0 z-10 w-40 px-3 py-2.5 font-medium align-middle border-b bg-n-slate-2 border-n-weak text-start"
              >
                {{
                  $t(
                    `BONBOZ.TUONG_TAC.COT_${gom === 'nhan_vien' ? 'NGUOI' : 'LUC'}`
                  )
                }}
              </th>
              <th
                v-if="soCotNhom.TT"
                :colspan="soCotNhom.TT"
                class="px-3 py-2 font-medium text-center border-b border-n-weak text-n-slate-12"
              >
                {{ $t('BONBOZ.TUONG_TAC.NHOM_TT') }}
              </th>
              <th
                v-if="soCotNhom.DH"
                :colspan="soCotNhom.DH"
                class="px-3 py-2 font-medium text-center border-b border-l border-n-weak text-n-slate-12"
              >
                {{ $t('BONBOZ.TUONG_TAC.NHOM_DH') }}
              </th>
            </tr>
            <!-- Tầng 2: tên từng cột. -->
            <tr class="text-xs border-b border-n-weak text-n-slate-11">
              <th
                v-for="c in cot"
                :key="c.khoa"
                v-tooltip.top="$t(`BONBOZ.TUONG_TAC.G_${c.khoa}`)"
                class="px-3 py-2 font-medium leading-snug align-bottom whitespace-nowrap text-end"
                :class="[
                  vien(c),
                  c.noi_bat ? 'border-t rounded-t text-n-slate-12' : '',
                ]"
              >
                {{ $t(`BONBOZ.TUONG_TAC.C_${c.khoa}`) }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="d in hang"
              :key="d.id ?? d.moc"
              class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
            >
              <td
                class="sticky left-0 px-3 py-2 whitespace-nowrap bg-n-solid-2 text-n-slate-12"
              >
                <span v-if="gom !== 'nhan_vien'">{{ nhanDong(d) }}</span>
                <span v-else class="flex items-center gap-2">
                  <Avatar :src="d.anh" :name="d.ten" :size="26" rounded-full />
                  <span class="truncate">{{ d.ten }}</span>
                </span>
              </td>
              <td
                v-for="c in cot"
                :key="c.khoa"
                class="px-3 py-2 tabular-nums text-end"
                :class="[
                  vien(c),
                  c.noi_bat ? 'font-semibold' : '',
                  oCua(d, c) ? 'text-n-slate-12' : 'text-n-slate-10',
                ]"
              >
                {{ oCua(d, c) ?? '—' }}
              </td>
            </tr>
          </tbody>

          <tfoot v-if="tong">
            <tr
              class="sticky bottom-0 text-sm font-semibold border-t bg-n-slate-2 border-n-weak text-n-slate-12"
            >
              <td class="sticky left-0 px-3 py-2.5 bg-n-slate-2">
                {{ $t('BONBOZ.TUONG_TAC.TONG_TT') }}
              </td>
              <td
                v-for="c in cot"
                :key="c.khoa"
                v-tooltip.top="
                  c.khoa.startsWith('tt_') || c.khoa === 'tong_tt'
                    ? $t('BONBOZ.TUONG_TAC.GT_TONG')
                    : undefined
                "
                class="px-3 py-2.5 tabular-nums text-end"
                :class="[vien(c), c.noi_bat ? 'border-b rounded-b' : '']"
              >
                {{ oCua(tong, c) ?? '—' }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <p class="px-4 py-2.5 m-0 text-xxs text-n-slate-11">
      {{ $t('BONBOZ.TUONG_TAC.GT_TONG') }}
    </p>
  </section>
</template>
